import { Storage as AuthStorage } from "./Storage";
import { TokensManager } from "./TokensManager";
import { ProofKeyForCodeExchange } from "./ProofKeyForCodeExchange";
import { User } from "./User";
import { joinURL, withQuery, getQuery } from "ufo";
import { getAuthenticationHeaderValue } from "../api";
import { encodeQuery } from "../utils";
import { defaultLang, allowedLangs } from "../constants/locales.constants";
export class Auth {
    #refreshTokensPromise;
    #refreshTokensCallbacks = [];
    #configuration;
    #axios;
    #storage;
    #pkce;
    tokens;
    user;
    constructor(configuration, axios) {
        this.#configuration = configuration;
        this.#axios = axios;
        this.#storage = new AuthStorage("platform.auth");
        this.tokens = new TokensManager(this.#storage);
        this.#pkce = new ProofKeyForCodeExchange(this.#storage);
    }
    get endpoints() {
        const base = joinURL(this.#configuration.host, "/auth/realms/", this.#configuration.realm, "/protocol/openid-connect");
        return {
            base,
            login: joinURL(base, "/auth"),
            token: joinURL(base, "/token"),
            profile: joinURL(base, "/userinfo"),
            logout: joinURL(base, "/logout"),
        };
    }
    get isLoggedIn() {
        this.tokens.sync();
        const { access, refresh } = this.tokens;
        return access && refresh && !refresh.isExpired;
    }
    login() {
        const { verifier, challenge } = this.#pkce.generate();
        const originQueryParams = JSON.stringify(getQuery(window.location.href));
        this.#storage.set("verifier", verifier);
        this.#storage.set("originQueryParams", originQueryParams);
        window.location.replace(withQuery(this.endpoints.login, {
            response_type: "code",
            client_id: this.#configuration.clientID,
            scope: this.#configuration.scope.join(" "),
            redirect_uri: window.location.href.replace(window.location.search, ""),
            code_challenge: challenge,
            code_challenge_method: "S256",
            ui_locales: new URLSearchParams(location.search).get("lang") || "ru",
        }));
    }
    async handleCallback() {
        const query = getQuery(window.location.href);
        const code = typeof query.code === "string" ? query.code : null;
        const verifier = this.#storage.get("verifier");
        const originQueryParams = JSON.parse(this.#storage.get("originQueryParams"));
        if (code && verifier) {
            await this.obtainCode(code, verifier);
        }
        this.#storage.remove("verifier");
        this.#storage.remove("originQueryParams");
        let params = new URLSearchParams(originQueryParams ?? location.search);
        params.delete("session_state");
        params.delete("code");
        const { access } = this.tokens;
        const lang = access?.decoded?.locale;
        if (allowedLangs.includes(lang)) {
            lang === defaultLang ? params.delete("lang") : params.set("lang", lang);
        }
        if (params && params.toString().length) {
            params = "?" + params.toString();
        }
        history.pushState(null, "", location.href.split("?")[0] + params);
    }
    async obtainCode(code, verifier) {
        const { data } = await this.#axios.post(this.endpoints.token, encodeQuery({
            code,
            response_type: "code",
            grant_type: "authorization_code",
            client_id: this.#configuration.clientID,
            redirect_uri: window.location.href.replace(window.location.search, ""),
            code_verifier: verifier,
        }), {
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
            },
        });
        this.tokens.setAccess(data.access_token);
        this.tokens.setRefresh(data.refresh_token);
    }
    async fetchUser() {
        if (!this.tokens.access) {
            console.warn("Impossible to fetch user profile, because access token does not exists");
            return;
        }
        try {
            const { data } = await this.#axios.get(this.endpoints.profile, {
                headers: {
                    Authorization: getAuthenticationHeaderValue(this.tokens.access.encoded),
                },
            });
            this.user = new User(data, this.tokens.access.decoded.resource_access[this.#configuration.resource]
                ?.roles || []);
        }
        catch (e) {
            this.logout(true);
        }
    }
    onTokensRefresh(callback) {
        this.#refreshTokensCallbacks.push(callback);
    }
    refreshTokens() {
        if (!this.tokens.refresh || this.tokens.refresh.isExpired) {
            return this.logout();
        }
        if (!this.#refreshTokensPromise) {
            const config = {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
                __disableTokensRefreshing: true,
            };
            const postData = encodeQuery({
                client_id: this.#configuration.clientID,
                resource: this.#configuration.resource,
                refresh_token: this.tokens.refresh.encoded,
                grant_type: "refresh_token",
            });
            this.#refreshTokensPromise = this.#axios
                .post(this.endpoints.token, postData, config)
                .then(({ data }) => {
                this.tokens.setAccess(data.access_token);
                this.tokens.setRefresh(data.refresh_token);
                this.#refreshTokensCallbacks.forEach((callback) => callback());
                return this.tokens;
            })
                .catch(() => this.logout(true))
                .finally(() => {
                this.#refreshTokensPromise = null;
            });
        }
        return this.#refreshTokensPromise;
    }
    async start() {
        await this.handleCallback();
        if (!this.isLoggedIn) {
            this.login();
            return;
        }
        await this.refreshTokens();
        await this.fetchUser();
    }
    async logout(redirect = false) {
        if (redirect)
            window.singleSpaNavigate("/");
        if (this.tokens.refresh) {
            await this.#axios.post(this.endpoints.logout, encodeQuery({
                client_id: this.#configuration.clientID,
                refresh_token: this.tokens.refresh.encoded,
            }), {
                headers: {
                    Authorization: getAuthenticationHeaderValue(this.tokens.access.encoded),
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            });
        }
        this.tokens.cleanOut();
        this.user = null;
        this.login();
    }
}
